/**
 * Shared combobox input utility functions used by both Elements and React packages.
 * Covers keyboard actions, focus-out validation, match checking, and input display.
 */

import type { IPktComboboxOption } from 'shared-types/combobox'
import { findOptionByValue, getSearchInfoMessage } from './option-utils'

// Keyboard handling

export type TInputKeyAction = 'addValue' | 'focusListbox' | 'closeOptions' | null

/**
 * Determines the action for a keydown event on the combobox input.
 */
export const getInputKeyAction = (
  key: string,
  shiftKey: boolean,
  multiple: boolean,
): TInputKeyAction => {
  switch (key) {
    case ',':
      return multiple ? 'addValue' : null
    case 'Enter':
      return 'addValue'
    case 'Tab':
    case 'ArrowDown':
      return shiftKey ? null : 'focusListbox'
    case 'Escape':
      return 'closeOptions'
    default:
      return null
  }
}

/**
 * Determines if a key is an arrow/toggle key for select-only mode.
 */
export const isArrowToggleKey = (key: string): boolean => {
  return key === 'Enter' || key === ' ' || key === 'ArrowDown'
}

// Focus-out validation

export type TFocusOutAction = 'addUserValue' | 'selectOption' | 'removeValue' | 'none'

export interface IFocusOutResult {
  action: TFocusOutAction
  value: string | null
}

/**
 * Determines what to do with the input value when focus leaves the combobox.
 */
export const getInputValueAction = (
  inputValue: string,
  currentValues: string[],
  options: IPktComboboxOption[],
  allowUserInput: boolean,
  multiple: boolean,
): IFocusOutResult => {
  if (!inputValue) return { action: 'none', value: null }

  const optionMatch = findOptionByValue(options, inputValue)

  if (!currentValues.includes(inputValue) && !optionMatch) {
    if (allowUserInput) {
      return { action: 'addUserValue', value: inputValue }
    }
    if (!multiple) {
      return { action: 'removeValue', value: currentValues[0] }
    }
    return { action: 'none', value: null }
  }

  if (optionMatch && !currentValues.includes(optionMatch.value)) {
    return { action: 'selectOption', value: optionMatch.value }
  }

  return { action: 'none', value: null }
}

// Match checking

export interface ICheckForMatchesResult {
  addValueText: string | null
  userInfoMessage: string
  shouldRemoveValue: boolean
  shouldResetInput: boolean
}

/**
 * Determines the info message and actions based on search input.
 * Wraps getSearchInfoMessage, adding the empty-input case.
 */
export const checkForMatches = (
  inputValue: string,
  currentValues: string[],
  options: IPktComboboxOption[],
  allowUserInput: boolean,
  multiple: boolean,
): ICheckForMatchesResult => {
  const searchValue = inputValue.trim().toLowerCase()

  if (!searchValue) {
    return {
      addValueText: null,
      userInfoMessage: '',
      shouldRemoveValue: !multiple && !!currentValues[0],
      shouldResetInput: true,
    }
  }

  const result = getSearchInfoMessage(inputValue, currentValues, options, allowUserInput)
  return {
    ...result,
    shouldRemoveValue: false,
    shouldResetInput: false,
  }
}

// Input display

/**
 * Determines what text to display in a single-select input for the given value.
 */
export const getSingleValueForInput = (
  value: string,
  options: IPktComboboxOption[],
  displayValueAs: string,
): string => {
  const option = findOptionByValue(options, value)
  if (displayValueAs === 'label' && option?.label) return option.label
  return value
}
